import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { tokenRefreshService } from '../services/tokenRefresh';

/**
 * Hook para manejar la renovación automática de tokens
 * Mantiene la sesión activa hasta que el usuario haga logout explícitamente
 */
export const useAutoTokenRefresh = () => {
  const { user, isAuthenticated, refreshUserToken } = useAuth();
  const refreshIntervalRef = useRef<number | null>(null);
  const isActiveRef = useRef(false);

  // Función para iniciar el monitoreo automático con refresh inteligente
  const startAutoRefresh = useCallback(() => {
    if (!isAuthenticated || !user || isActiveRef.current) {
      return;
    }

    console.log('🔄 Iniciando renovación automática de tokens (inteligente)...');
    isActiveRef.current = true;

    // Verificar token cada 2 minutos usando el sistema inteligente
    // El sistema inteligente solo hará petición a Firebase si es necesario
    refreshIntervalRef.current = setInterval(async () => {
      try {
        console.log('🔍 Verificando token automáticamente (inteligente)...');
        await tokenRefreshService.refreshTokenIfNeeded();
        console.log('✅ Token verificado automáticamente');
      } catch (error) {
        console.error('❌ Error en verificación automática:', error);
      }
    }, 2 * 60 * 1000) as unknown as number; // 2 minutos
  }, [isAuthenticated, user]);

  // Función para detener el monitoreo automático
  const stopAutoRefresh = useCallback(() => {
    console.log('🛑 Deteniendo renovación automática de tokens...');
    isActiveRef.current = false;
    
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Efecto para manejar el ciclo de vida del auto-refresh
  useEffect(() => {
    if (isAuthenticated && user) {
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }

    // Cleanup al desmontar
    return () => {
      stopAutoRefresh();
    };
  }, [isAuthenticated, user, startAutoRefresh, stopAutoRefresh]);

  // Efecto para manejar la visibilidad de la página
  // DESHABILITADO TEMPORALMENTE para evitar demasiadas peticiones
  // useEffect(() => {
  //   let lastVisibilityCheck = 0;
  //   const MIN_VISIBILITY_INTERVAL = 30 * 1000; // 30 segundos mínimo entre verificaciones

  //   const handleVisibilityChange = () => {
  //     if (document.visibilityState === 'visible' && isAuthenticated && user) {
  //       const now = Date.now();
  //       if (now - lastVisibilityCheck > MIN_VISIBILITY_INTERVAL) {
  //         console.log('👁️ Página visible - Verificando token...');
  //         lastVisibilityCheck = now;
  //         // Verificar token cuando la página vuelve a ser visible
  //         refreshUserToken().catch(error => {
  //           console.error('❌ Error verificando token al volver visible:', error);
  //         });
  //       }
  //     }
  //   };

  //   // Escuchar cambios de visibilidad
  //   document.addEventListener('visibilitychange', handleVisibilityChange);

  //   return () => {
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //   };
  // }, [isAuthenticated, user]);

  // Efecto para manejar el focus de la ventana
  // DESHABILITADO TEMPORALMENTE para evitar demasiadas peticiones
  // useEffect(() => {
  //   let lastFocusCheck = 0;
  //   const MIN_FOCUS_INTERVAL = 30 * 1000; // 30 segundos mínimo entre verificaciones

  //   const handleWindowFocus = () => {
  //     if (isAuthenticated && user) {
  //       const claims = tokenResult.claims as any;
  //       const now = Date.now();
  //       if (now - lastFocusCheck > MIN_FOCUS_INTERVAL) {
  //         console.log('🪟 Ventana enfocada - Verificando token...');
  //         lastFocusCheck = now;
  //         // Verificar token cuando la ventana recibe focus
  //         refreshUserToken().catch(error => {
  //           console.error('❌ Error verificando token al enfocar ventana:', error);
  //         });
  //       }
  //     }
  //   };

  //   // Escuchar eventos de focus
  //   window.addEventListener('focus', handleWindowFocus);

  //   return () => {
  //     window.removeEventListener('focus', handleWindowFocus);
  //   };
  // }, [isAuthenticated, user]);

  return {
    isActive: isActiveRef.current,
    startAutoRefresh,
    stopAutoRefresh,
    refreshNow: () => refreshUserToken()
  };
}; 